import React, { useState } from "react";
import { BadgePoundSterling, Scale, Ban, Users, Phone, Mail, Check } from "lucide-react";
import { whyPoints, PHONE, PHONE_TEL, EMAIL } from "../mock";
import { useToast } from "../hooks/use-toast";

const iconMap = { BadgePoundSterling, Scale, Ban, Users };

const WhySection = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sent, setSent] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    // Mocked submission (stored locally for now)
    const existing = JSON.parse(localStorage.getItem("t4tech_callbacks") || "[]");
    existing.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("t4tech_callbacks", JSON.stringify(existing));
    setSent(true);
    toast({ title: "Callback requested", description: "A UK advisor will be in touch shortly. It's completely free." });
    setForm({ name: "", phone: "", email: "", message: "" });
    setTimeout(() => setSent(false), 4000);
  };

  const field = (name) => ({
    value: form[name],
    onChange: (e) => setForm((f) => ({ ...f, [name]: e.target.value })),
  });

  return (
    <section id="contact" className="bg-[#1e183a] py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <p className="reveal text-xs font-semibold tracking-[0.22em] uppercase text-[#7c3aed] mb-4">
          Why T4TECH
        </p>
        <h2 className="reveal font-display text-4xl lg:text-5xl font-semibold text-white leading-tight max-w-2xl">
          Genuinely on your side.
        </h2>
        <p className="reveal mt-6 text-white/60 leading-relaxed max-w-2xl">
          We're an independent, third-party support service &mdash; not a network.
          That means clear, honest advice with nothing to sell you.
        </p>

        <div className="mt-14 grid lg:grid-cols-2 gap-12 items-start">
          {/* left: points */}
          <div className="grid sm:grid-cols-2 gap-5">
            {whyPoints.map((p) => {
              const Icon = iconMap[p.icon];
              return (
                <div
                  key={p.title}
                  className="reveal rounded-2xl bg-white/[0.04] border border-white/10 p-6 transition-colors hover:bg-white/[0.07]"
                >
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-[#7c3aed] text-white mb-4">
                    <Icon className="w-5 h-5" strokeWidth={2.2} />
                  </span>
                  <h3 className="font-display text-lg font-semibold text-white">{p.title}</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
            <a
              href={`tel:${PHONE_TEL}`}
              className="reveal sm:col-span-2 rounded-2xl bg-[#7c3aed] p-6 flex items-center justify-between transition-all hover:-translate-y-0.5"
            >
              <div>
                <p className="text-white/80 text-sm font-medium">Call Free · 7 Days a Week</p>
                <p className="font-display text-2xl font-semibold text-white">{PHONE}</p>
              </div>
              <span className="grid place-items-center w-12 h-12 rounded-full bg-white/20 text-white">
                <Phone className="w-5 h-5" strokeWidth={2.5} />
              </span>
            </a>
          </div>

          {/* right: callback form */}
          <div className="reveal rounded-3xl bg-white p-8 lg:p-10">
            <h3 className="font-display text-2xl font-semibold text-[#1e183a]">
              Prefer we call you back?
            </h3>
            <p className="mt-2 text-[#1e183a]/60 text-sm leading-relaxed">
              Leave your details and a friendly UK advisor will get in touch. It's
              completely free.
            </p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <input
                required
                placeholder="Your name"
                {...field("name")}
                className="w-full rounded-xl border border-[#1e183a]/15 bg-[#f4f2fb]/50 px-4 py-3.5 text-[#1e183a] placeholder:text-[#1e183a]/40 outline-none focus:border-[#7c3aed] focus:ring-2 focus:ring-[#7c3aed]/20 transition"
              />
              <input
                required
                placeholder="Phone number"
                {...field("phone")}
                className="w-full rounded-xl border border-[#1e183a]/15 bg-[#f4f2fb]/50 px-4 py-3.5 text-[#1e183a] placeholder:text-[#1e183a]/40 outline-none focus:border-[#7c3aed] focus:ring-2 focus:ring-[#7c3aed]/20 transition"
              />
              <input
                type="email"
                placeholder="Email address"
                {...field("email")}
                className="w-full rounded-xl border border-[#1e183a]/15 bg-[#f4f2fb]/50 px-4 py-3.5 text-[#1e183a] placeholder:text-[#1e183a]/40 outline-none focus:border-[#7c3aed] focus:ring-2 focus:ring-[#7c3aed]/20 transition"
              />
              <textarea
                rows={3}
                placeholder="How can we help?"
                {...field("message")}
                className="w-full rounded-xl border border-[#1e183a]/15 bg-[#f4f2fb]/50 px-4 py-3.5 text-[#1e183a] placeholder:text-[#1e183a]/40 outline-none focus:border-[#7c3aed] focus:ring-2 focus:ring-[#7c3aed]/20 transition resize-none"
              />
              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#7c3aed] hover:bg-[#6d28d9] text-white font-semibold px-6 py-4 transition-all hover:-translate-y-0.5"
              >
                {sent ? (
                  <><Check className="w-5 h-5" /> Request Sent</>
                ) : (
                  "Request a Free Callback"
                )}
              </button>
              <a
                href={`mailto:${EMAIL}`}
                className="flex items-center justify-center gap-2 text-sm text-[#1e183a]/60 hover:text-[#7c3aed] transition-colors"
              >
                <Mail className="w-4 h-4" /> Or email {EMAIL}
              </a>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhySection;
